import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Plus, Inbox, Loader2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { StatusBadge, LevelBadge, VT_LABELS } from "@/components/TicketBits";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const TicketRow = ({ t, testid, action }) => (
  <div data-testid={testid} className="glass-card rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Link to={`/ticket/${t.ticket_id}`} data-testid={`ticket-link-${t.ticket_id}`} className="font-display font-bold text-white hover:text-volt transition-colors truncate">
          {t.title}
        </Link>
        <StatusBadge status={t.status} />
        <LevelBadge level={t.level} label={t.level_label} />
      </div>
      <p className="font-mono text-xs text-slate-500 mt-2">
        {VT_LABELS[t.vehicle_type]} {t.vehicle_info && `· ${t.vehicle_info}`} {t.dtc_codes && `· DTC: ${t.dtc_codes}`}
      </p>
      <p className="text-xs text-slate-600 mt-1">
        {new Date(t.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })} · €{t.price_eur}
        {t.expert_name && ` · Esperto: ${t.expert_name}`}
      </p>
    </div>
    {action}
  </div>
);

export default function Dashboard() {
  const { user, loading, authHeader } = useAuth();
  const [tickets, setTickets] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api
      .get("/tickets", { headers: authHeader() })
      .then((r) => setTickets(r.data))
      .catch(() => toast.error("Errore nel caricamento dei ticket"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <Loader2 className="text-volt animate-spin" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/accedi" replace />;

  const assign = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/assign`, {}, { headers: authHeader() });
      toast.success("Ticket preso in carico! Apri la chat per rispondere.");
      load();
      navigate(`/ticket/${ticketId}`);
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Operazione non riuscita");
      load();
    }
  };

  const isCliente = user.role === "cliente";
  const available = (tickets || []).filter((t) => t.status === "open");
  const mine = (tickets || []).filter((t) => (isCliente ? true : t.expert_id === user.user_id));

  return (
    <div className="min-h-screen bg-obsidian" data-testid="dashboard-page">
      <Nav />
      <main className="px-4 sm:px-8 max-w-5xl mx-auto pt-28 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow mb-2">{isCliente ? "Area Cliente" : "Area Esperto"}</p>
            <h1 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">
              Ciao, {user.name.split(" ")[0]}
            </h1>
          </div>
          {isCliente && (
            <button
              data-testid="new-ticket-button"
              onClick={() => navigate("/nuovo-ticket")}
              className="flex items-center justify-center gap-2 bg-volt text-obsidian font-semibold px-6 py-3.5 rounded-full hover:bg-spark hover:scale-[1.02] transition-all duration-200"
            >
              <Plus size={16} /> Nuovo Ticket
            </button>
          )}
        </div>

        {tickets === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="text-volt animate-spin" size={28} />
          </div>
        ) : (
          <>
            {!isCliente && (
              <section className="mb-12" data-testid="available-tickets-section">
                <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400 mb-4 flex items-center gap-2">
                  <Inbox size={14} className="text-volt" /> Ticket disponibili ({available.length})
                </h2>
                {available.length === 0 ? (
                  <p className="text-slate-600 font-mono text-sm glass-card rounded-xl p-6">
                    Nessun ticket aperto al momento. I nuovi ticket pagati appariranno qui.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {available.map((t) => (
                      <TicketRow
                        key={t.ticket_id}
                        t={t}
                        testid={`available-ticket-${t.ticket_id}`}
                        action={
                          <button
                            data-testid={`assign-ticket-${t.ticket_id}`}
                            onClick={() => assign(t.ticket_id)}
                            className="shrink-0 bg-volt text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-spark transition-colors"
                          >
                            Prendi in carico · €{(t.price_eur * 0.8).toFixed(0)} netti
                          </button>
                        }
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            <section data-testid="my-tickets-section">
              <h2 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400 mb-4 flex items-center gap-2">
                <Briefcase size={14} className="text-volt" /> {isCliente ? `I miei ticket (${mine.length})` : `Ticket in carico (${mine.length})`}
              </h2>
              {mine.length === 0 ? (
                <div className="glass-card rounded-xl p-8 text-center">
                  <p className="text-slate-500 text-sm">
                    {isCliente ? "Nessun ticket ancora. Apri il primo e descrivici il problema." : "Non hai ticket in carico."}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {mine.map((t) => (
                    <TicketRow
                      key={t.ticket_id}
                      t={t}
                      testid={`my-ticket-${t.ticket_id}`}
                      action={
                        <Link
                          to={`/ticket/${t.ticket_id}`}
                          data-testid={`open-ticket-${t.ticket_id}`}
                          className="shrink-0 border border-white/15 text-slate-300 text-sm px-5 py-2.5 rounded-full hover:border-volt hover:text-volt transition-colors text-center"
                        >
                          Apri
                        </Link>
                      }
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
